/*
 * Pure H OS — Certificate Verification Page (public, no auth)
 * Rendered when someone scans the QR on an Investment Certificate PDF.
 * Fetches metadata from GET /api/pureh/investors/verify/certificate/:certNo
 * Executive luxury style — ivory + ink + gold hairlines. Matches PDF language.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n, ccy = "EGP") =>
  n === null || n === undefined || n === ""
    ? "—"
    : `${ccy} ${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
};

const shell = {
  minHeight: "100vh",
  background: "#faf8f4",
  color: "#171719",
  fontFamily: '-apple-system, "SF Pro Text", "Inter", system-ui, sans-serif',
  padding: "32px 16px 48px",
};
const card = {
  maxWidth: 780,
  margin: "0 auto",
  background: "#ffffff",
  borderRadius: 8,
  boxShadow: "0 24px 60px -30px rgba(23,23,25,0.16), 0 2px 6px rgba(23,23,25,0.04)",
  padding: "48px 56px 40px",
};
const eyebrow = {
  color: "#7f6420",
  fontSize: 10,
  letterSpacing: "0.24em",
  fontWeight: 700,
  textTransform: "uppercase",
};
const title = {
  fontFamily: '"Times New Roman", "PT Serif", serif',
  fontSize: 38,
  lineHeight: 1.1,
  fontWeight: 700,
  margin: "10px 0 4px",
};
const hairline = { height: 1, background: "#dfd3ad", margin: "20px 0" };
const rowLabel = {
  fontSize: 10,
  letterSpacing: "0.16em",
  fontWeight: 700,
  color: "#7f6420",
  textTransform: "uppercase",
  marginBottom: 4,
};
const rowValue = { fontSize: 15, fontWeight: 500, color: "#171719" };
const legalStrip = {
  marginTop: 32,
  paddingTop: 14,
  borderTop: "1px solid #e7e3d2",
  fontSize: 11,
  color: "#5a5a5c",
  lineHeight: 1.5,
};

export default function CertificateVerify() {
  const { certNo } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pureh/investors/verify/certificate/${encodeURIComponent(certNo)}`);
        const j = await r.json();
        if (!cancelled) setState({ loading: false, data: j, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [certNo]);

  if (state.loading) {
    return (
      <div style={shell}>
        <div style={{ ...card, textAlign: "center", padding: "80px 40px" }}
             data-testid="cert-verify-loading">
          <div style={eyebrow}>Pure H · Executive registry</div>
          <div style={{ ...title, fontSize: 24 }}>Verifying certificate…</div>
        </div>
      </div>
    );
  }

  const d = state.data || {};
  const isValid = d.valid !== false && (d.status === "valid" || d.status === undefined);

  return (
    <div style={shell} data-testid="cert-verify-page">
      <div style={card}>
        {/* Header lockup */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={eyebrow}>Pure H · Certificate Verification</div>
            <div style={title}>{isValid ? "Valid Certificate" : "Certificate Not Found"}</div>
            <div style={{ color: "#5a5a5c", fontSize: 13, marginTop: 2 }}>
              {isValid
                ? "This certificate has been issued and registered by Pure H OS."
                : (d.message || "This certificate could not be located in the Pure H registry.")}
            </div>
          </div>

          {/* Status pill */}
          <div
            data-testid="cert-verify-status-pill"
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 11,
              letterSpacing: "0.14em",
              fontWeight: 700,
              textTransform: "uppercase",
              background: isValid ? "#f4edd9" : "#fbe6e6",
              color: isValid ? "#7f6420" : "#8a2b2b",
              border: `1px solid ${isValid ? "#dfd3ad" : "#e8bcbc"}`,
              whiteSpace: "nowrap",
            }}
          >
            {isValid ? "Valid" : "Invalid"}
          </div>
        </div>

        <div style={hairline} />

        {/* Metadata grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 40px" }}>
          <Field label="Certificate №" value={d.certificateNo || certNo} testid="cert-verify-number" />
          <Field label="Verification status" value={isValid ? "Valid" : "Invalid"} testid="cert-verify-status" />
          <Field label="Investor" value={d.investorName || "—"} testid="cert-verify-investor" />
          <Field label="Investor code" value={d.investorCode || "—"} />
          <Field label="Venture" value={d.ventureName || d.ventureId || "—"} testid="cert-verify-venture" />
          <Field label="Ownership" value={d.ownershipPct != null ? `${Number(d.ownershipPct).toFixed(2)}%` : "—"} testid="cert-verify-ownership" />
          <Field label="Capital paid" value={fmtMoney(d.capitalPaid, d.currency)} testid="cert-verify-paid" />
          <Field label="Capital committed" value={fmtMoney(d.capitalCommitted, d.currency)} testid="cert-verify-committed" />
          <Field label="Issue date" value={fmtDate(d.issuedAt)} testid="cert-verify-issued" />
          <Field label="Verified at" value={fmtDate(d.verifiedAt)} />
        </div>

        <div style={legalStrip}>
          <div style={{ fontWeight: 700, color: "#171719" }}>
            {d.companyLegal ? `${d.companyNameEn || ""} ${d.companyLegal}`.trim() : "Pure H Holding"}
          </div>
          <div>Generated from {d.generatedFrom || "Pure H OS"} · Executive registry</div>
          <div style={{ marginTop: 8 }}>
            If any field above does not match the printed certificate, please contact the issuer.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div style={rowLabel}>{label}</div>
      <div style={rowValue} data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}
